import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Palette, Vibrate, Volume2, Sparkles, Accessibility, User, Bell,
  Wallet, ShieldAlert, Save, CreditCard, Sun, Moon, Monitor, Trophy, KeyRound,
  Camera, Trash2, Loader2,
} from "lucide-react";
import api, { apiError } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const avatarSrc = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { enablePush, disablePush, getPushStatus, isPushSupported, needsHomeScreenInstall } from "@/lib/push";
import { AdInterests } from "@/components/AdInterests";
import dayjs from "dayjs";

/* ---- small building blocks ---- */
const Section = ({ icon: Icon, title, desc, children }) => (
  <section className="bg-race-surface border border-race-line rounded-lg p-5 sm:p-6">
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-md bg-race-elevated flex items-center justify-center shrink-0">
        <Icon size={20} className="text-race-txt" />
      </div>
      <div>
        <h2 className="font-heading text-2xl leading-none">{title}</h2>
        {desc && <p className="text-sm text-race-sub mt-1">{desc}</p>}
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const Row = ({ label, hint, children, testid }) => (
  <div className="flex items-center justify-between gap-4 py-2" data-testid={testid}>
    <div className="min-w-0">
      <div className="text-sm font-medium text-race-txt">{label}</div>
      {hint && <div className="text-xs text-race-sub mt-0.5">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const Toggle = ({ on, onChange, testid }) => (
  <button type="button" data-testid={testid} onClick={() => onChange(!on)}
    role="switch" aria-checked={on}
    className={`relative w-12 h-7 rounded-full transition-colors ${on ? "bg-race-red" : "bg-race-elevated border border-race-line"}`}>
    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full transition-transform ${on ? "translate-x-5 bg-race-bg" : "translate-x-0 bg-race-sub"}`} />
  </button>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline block mb-1">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-2.5 rounded-sm text-race-txt";

function LastSmsStatus() {
  const [last, setLast] = useState(undefined);
  useEffect(() => { api.get("/me/sms-status").then((r) => setLast(r.data.last)).catch(() => setLast(null)); }, []);
  if (!last) return null;
  const bad = ["failed", "undelivered"].includes(last.status);
  const good = ["delivered", "read"].includes(last.status);
  const at = new Date(last.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <div className={`mt-2 ml-8 text-xs font-mono ${bad ? "text-red-500" : good ? "text-green-500" : "text-race-sub"}`} data-testid="profile-last-sms">
      Last race-reminder text: {bad ? "Failed" : good ? "Delivered" : "Sending"} · {at}
      {bad && last.error ? <span className="block text-race-sub mt-0.5">{last.sender_issue ? "Our texting service is still being set up - nothing to fix on your side. You'll still get push + email reminders." : `${last.error}. Check your number above, or contact IBRA support.`}</span> : null}
    </div>
  );
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { settings, update } = useSettings();

  const [name, setName] = useState(user?.name || "");
  const [provider, setProvider] = useState(user?.cash_provider || "");
  const [phone, setPhone] = useState(user?.payout_phone || "");
  const [reminder, setReminder] = useState(user?.reminder_lead || 3);
  const [deposit, setDeposit] = useState(user?.deposit_limit_credits ?? 0);
  const [selfExcluded, setSelfExcluded] = useState(!!user?.self_excluded);
  const [smsOptIn, setSmsOptIn] = useState(!!user?.sms_opt_in);
  const [saving, setSaving] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [pushStatus, setPushStatus] = useState("default");   // unsupported|denied|enabled|default
  const [pushBusy, setPushBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const fileRef = React.useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return toast.error("Please choose a JPG, PNG or WebP image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image is too large (max 5MB)");
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Profile photo updated");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setAvatarBusy(false); }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      await api.delete("/me/avatar");
      await refreshUser();
      toast.success("Profile photo removed");
    } catch (err) { toast.error("Could not remove photo"); }
    finally { setAvatarBusy(false); }
  };

  const changePassword = async () => {
    if (pw.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.next !== pw.confirm) return toast.error("New passwords don't match");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  useEffect(() => {
    api.get("/me/earnings").then((r) => setEarnings(r.data)).catch(() => {});
    getPushStatus().then(setPushStatus).catch(() => {});
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushStatus === "enabled") {
        await disablePush();
        setPushStatus("default");
        toast.success("Push reminders turned off on this device");
      } else {
        await enablePush();
        setPushStatus("enabled");
        toast.success("Push reminders enabled on this device");
        try { await api.post("/push/test"); } catch (e) { /* non-fatal */ }
      }
    } catch (e) {
      toast.error(e.message || "Could not update push notifications");
      setPushStatus(await getPushStatus());
    } finally {
      setPushBusy(false);
    }
  };

  const sendTestPush = async () => {
    setPushBusy(true);
    try {
      await api.post("/push/test");
      toast.success("Test sent - check your notifications");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Couldn't send a test push");
    } finally {
      setPushBusy(false);
    }
  };

  const themeOpts = [
    { v: "light", label: "Light", Icon: Sun },
    { v: "dark", label: "Dark", Icon: Moon },
    { v: "system", label: "System", Icon: Monitor },
  ];

  const saveAccount = async () => {
    setSaving(true);
    try {
      await api.patch("/me", {
        name,
        cash_provider: provider,
        payout_phone: phone,
        reminder_lead: Number(reminder),
        deposit_limit_credits: Number(deposit),
        self_excluded: selfExcluded,
        sms_opt_in: smsOptIn,
      });
      await refreshUser();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto animate-slide-up" data-testid="profile-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => !avatarBusy && fileRef.current?.click()}
            data-testid="avatar-upload-btn"
            aria-label="Change profile photo"
            className="group relative w-14 h-14 rounded-full overflow-hidden bg-race-red text-race-bg flex items-center justify-center font-heading text-3xl focus:outline-none focus:ring-2 focus:ring-race-red">
            {user.profile_image_url ? (
              <img src={avatarSrc(user.profile_image_url)} alt={user.name} className="w-full h-full object-cover" data-testid="avatar-image" />
            ) : (
              <span>{(user.name || "R").slice(0, 1).toUpperCase()}</span>
            )}
            <span className={`absolute inset-0 flex items-center justify-center bg-black/50 text-white transition-opacity ${avatarBusy ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
              {avatarBusy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickAvatar} className="hidden" data-testid="avatar-file-input" />
        </div>
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none">{user.name}</h1>
          <p className="text-sm text-race-sub mt-1">{user.email} · <span className="uppercase" data-testid="profile-roles">{(user.roles && user.roles.length ? user.roles : [user.role]).join(" · ")}</span></p>
          <div className="flex items-center gap-3 mt-1.5">
            <button type="button" onClick={() => !avatarBusy && fileRef.current?.click()} disabled={avatarBusy} data-testid="avatar-change-link"
              className="text-xs text-race-red hover:underline disabled:opacity-50">
              {user.profile_image_url ? "Change photo" : "Add photo"}
            </button>
            {user.profile_image_url && (
              <button type="button" onClick={removeAvatar} disabled={avatarBusy} data-testid="avatar-remove-btn"
                className="text-xs text-race-sub hover:text-race-red inline-flex items-center gap-1 disabled:opacity-50">
                <Trash2 size={12} /> Remove
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-5">
        {/* APPEARANCE */}
        <Section icon={Palette} title="Appearance" desc="Choose how Text Racing looks on this device.">
          <div className="grid grid-cols-3 gap-2" data-testid="theme-switch">
            {themeOpts.map(({ v, label, Icon }) => (
              <button key={v} data-testid={`theme-${v}`} onClick={() => update("theme", v)}
                className={`flex flex-col items-center gap-2 py-4 rounded-md border transition-colors ${
                  settings.theme === v ? "border-race-red bg-race-elevated text-race-txt" : "border-race-line text-race-sub hover:text-race-txt"
                }`}>
                <Icon size={22} />
                <span className="font-mono text-[11px] uppercase tracking-wide">{label}</span>
              </button>
            ))}
          </div>
        </Section>

        {/* GAMEPLAY & ACCESSIBILITY */}
        <Section icon={Sparkles} title="Race Feel & Accessibility" desc="Tune the feedback during a race.">
          <Row label="Haptic feedback" hint="Vibrate on correct keys and error penalties (supported devices)." testid="row-haptics">
            <Toggle on={settings.haptics === "on"} onChange={(v) => update("haptics", v ? "on" : "off")} testid="toggle-haptics" />
          </Row>
          <Row label="Sound effects" hint="Countdown ticks and finish tones." testid="row-sound">
            <Toggle on={settings.sound === "on"} onChange={(v) => update("sound", v ? "on" : "off")} testid="toggle-sound" />
          </Row>
          <Row label="Reduced motion" hint="Minimize animations across the app." testid="row-motion">
            <Toggle on={settings.reducedMotion === "on"} onChange={(v) => update("reducedMotion", v ? "on" : "off")} testid="toggle-motion" />
          </Row>
          <Row label="Larger keyboard keys" hint="Bigger, higher-contrast keys for easier thumb typing." testid="row-largekeys">
            <Toggle on={settings.largeKeys === "on"} onChange={(v) => update("largeKeys", v ? "on" : "off")} testid="toggle-largekeys" />
          </Row>
          <Row label="Ghost racers (practice only)" hint="Race an AI field in practice. Real IBRA contests are always humans-only." testid="row-ghosts">
            <Toggle on={settings.ghostRacers !== "off"} onChange={(v) => update("ghostRacers", v ? "on" : "off")} testid="toggle-ghosts" />
          </Row>
          <Row label="Keyboard light-up (practice only)" hint="Highlights the next key while practicing. Off in real contests." testid="row-guidance">
            <Toggle on={settings.keyGuidance !== "off"} onChange={(v) => update("keyGuidance", v ? "on" : "off")} testid="toggle-guidance" />
          </Row>
        </Section>

        {/* ACCOUNT */}
        <Section icon={User} title="Account" desc="Your racer identity.">
          <Field label="Racer Name">
            <input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} maxLength={40} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <span className="overline block mb-1">Email</span>
              <div className="px-3 py-2.5 rounded-sm bg-race-bg border border-race-line text-race-sub break-all">{user.email}</div>
            </div>
            <div>
              <span className="overline block mb-1">Member Since</span>
              <div className="px-3 py-2.5 rounded-sm bg-race-bg border border-race-line text-race-sub">{user.created_at ? dayjs(user.created_at).format("MMM D, YYYY") : "-"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CreditCard size={16} className={user.has_license ? "text-race-red" : "text-race-muted"} />
            {user.has_license ? <span className="text-race-txt">BR License active</span> : <span className="text-race-sub">No BR License yet</span>}
            <span className="ml-auto tabular text-race-sub">{user.credits ?? 0} credits</span>
          </div>
        </Section>

        {/* PRIZE EARNINGS */}
        <Section icon={KeyRound} title="Password" desc="Change the password you use to sign in.">
          <div className="space-y-3 max-w-sm">
            <input type="password" className={inputCls} placeholder="Current password" autoComplete="current-password"
              value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" />
            <input type="password" className={inputCls} placeholder="New password (min 6 chars)" autoComplete="new-password"
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new" />
            <input type="password" className={inputCls} placeholder="Confirm new password" autoComplete="new-password"
              value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" />
            <button onClick={changePassword} disabled={pwBusy || !pw.current || !pw.next} data-testid="pw-submit"
              className="bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-mono text-sm uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors">
              {pwBusy ? "Updating…" : "Update Password"}
            </button>
          </div>
        </Section>

        <Section icon={Trophy} title="Prize Earnings" desc="Your lifetime winnings. This total never decreases, even after a cash prize is paid out.">
          <div className="flex items-baseline justify-between" data-testid="earnings-total">
            <span className="text-sm text-race-sub">Total earned</span>
            <span className="tabular font-heading text-4xl text-race-volt">{(earnings?.total ?? 0).toLocaleString()} <span className="text-lg text-race-sub">credits</span></span>
          </div>
          <div className="text-xs text-race-sub">{earnings?.wins ?? 0} race win{(earnings?.wins ?? 0) === 1 ? "" : "s"}</div>
          {earnings?.history?.length > 0 ? (
            <div className="mt-2 divide-y divide-race-line/60 border-t border-race-line/60" data-testid="earnings-history">
              {earnings.history.map((h, i) => (
                <div key={i} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-race-txt truncate">{h.contest_name || "Contest"}</span>
                  <span className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-race-sub">{h.created_at ? dayjs(h.created_at).format("MMM D, YYYY") : ""}</span>
                    <span className="tabular text-race-volt">+{(h.amount || 0).toLocaleString()}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-race-muted">No prize winnings yet - win a cash contest to start earning.</p>
          )}
        </Section>

        {/* NOTIFICATIONS */}
        <Section icon={Bell} title="Race Reminders" desc="Get pinged before the green flag with a one-tap link to the Start-Line.">
          <Row label="Push notifications on this device"
               hint={pushStatus === "unsupported"
                 ? (needsHomeScreenInstall()
                     ? "On iPhone: tap Share → Add to Home Screen, open it from there, then enable."
                     : "This browser doesn't support push notifications.")
                 : pushStatus === "denied"
                   ? "Blocked in your browser settings - allow notifications for this site to enable."
                   : pushStatus === "enabled"
                     ? "You'll get a heads-up here before each race you've entered."
                     : "Turn on to race reminders straight to your lock screen."}
               testid="row-push">
            <button type="button" data-testid="btn-toggle-push" onClick={togglePush}
              disabled={pushBusy || pushStatus === "unsupported" || pushStatus === "denied"}
              className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-40 ${pushStatus === "enabled" ? "border border-race-line text-race-txt hover:border-race-red" : "bg-race-red text-race-bg hover:opacity-90"}`}>
              {pushBusy ? "…" : pushStatus === "enabled" ? "Turn off" : "Enable"}
            </button>
          </Row>
          {pushStatus === "enabled" && (
            <Row label="Send a test notification" hint="Fires a push to this device right now so you can confirm delivery." testid="row-push-test">
              <button type="button" data-testid="btn-send-test-push" onClick={sendTestPush} disabled={pushBusy}
                className="px-4 py-2 rounded-sm text-sm font-medium border border-race-line text-race-txt hover:border-race-red transition-colors disabled:opacity-40">
                {pushBusy ? "…" : "Send test"}
              </button>
            </Row>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Remind me before start">
              <select data-testid="select-reminder" value={reminder} onChange={(e) => setReminder(e.target.value)} className={inputCls}>
                <option value={3}>3 minutes</option>
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
              </select>
            </Field>
            <Field label="Mobile number">
              <input data-testid="input-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 1234" className={inputCls} />
            </Field>
          </div>
          <label className="flex items-start gap-3 mt-1 text-sm cursor-pointer" data-testid="row-sms-consent">
            <input type="checkbox" data-testid="checkbox-sms-consent" checked={smsOptIn}
              onChange={(e) => setSmsOptIn(e.target.checked)}
              className="mt-0.5 w-5 h-5 accent-race-red shrink-0" />
            <span className="text-race-txt">
              Yes, I agree to receive automated SMS race reminders from Text Racing at the number above.
              Message &amp; data rates may apply; reply STOP to opt out, HELP for help. Consent isn’t required
              to enter contests. See our{" "}
              <a href="/messaging-policy" target="_blank" rel="noreferrer" className="text-race-red underline">Messaging Policy</a>.
            </span>
          </label>
          {smsOptIn && <LastSmsStatus />}
        </Section>

        {/* PAYOUT */}
        <Section icon={Wallet} title="Payout" desc="Where your winnings are sent.">
          <Field label="Preferred cash payment provider">
            <select data-testid="select-provider" value={provider} onChange={(e) => setProvider(e.target.value)} className={inputCls}>
              <option value="">Not set</option>
              <option value="paypal">PayPal</option>
              <option value="venmo">Venmo</option>
              <option value="cashapp">Cash App</option>
              <option value="ach">Bank / ACH</option>
            </select>
          </Field>
          <p className="text-xs text-race-sub">Identity verification (KYC) is required before any payout is released.</p>
        </Section>

        {/* RESPONSIBLE GAMING */}
        <Section icon={ShieldAlert} title="Responsible Gaming" desc="Stay in control of your play.">
          <Field label="Weekly deposit limit (credits · 0 = no limit)">
            <input data-testid="input-deposit" type="number" min={0} value={deposit} onChange={(e) => setDeposit(e.target.value)} className={inputCls} />
          </Field>
          <Row label="Self-exclude from cash races" hint="Practice stays available; paid entries are blocked." testid="row-selfexclude">
            <Toggle on={selfExcluded} onChange={setSelfExcluded} testid="toggle-selfexclude" />
          </Row>
        </Section>

        <button onClick={saveAccount} disabled={saving} data-testid="profile-save"
          className="flex items-center justify-center gap-2 bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm transition-colors">
          <Save size={18} /> {saving ? "Saving…" : "Save Changes"}
        </button>

        {/* AD INTERESTS (interest-based ad network) */}
        <AdInterests />

        <p className="text-center text-xs text-race-muted pb-4">Appearance & race-feel settings save instantly on this device.</p>
      </div>
    </div>
  );
}
